/**
 * 🗺️ YACHT DESTINATIONS MANAGEMENT PAGE
 * Admin interface for managing destination-based pricing catalog
 * Hierarchical view: Countries → Cities → Destinations
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  TextField,
  InputAdornment,
  Grid,
  Paper,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as PublicIcon,
  LocationCity as LocationCityIcon,
  Place as PlaceIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  DirectionsBoat as BoatIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Map as MapIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { yachtDestinationsService, YachtDestination, Country, City } from '@/services/yachtDestinationsService';
import toast from 'react-hot-toast';

interface DestinationFormData {
  country: string;
  city: string;
  destinationName: string;
  description: string;
  address: string;
  latitude: string;
  longitude: string;
  estimatedDistanceKm: string;
}

export const YachtDestinationsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [destinations, setDestinations] = useState<YachtDestination[]>([]);
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactiveOnly, setShowInactiveOnly] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState<YachtDestination | null>(null);
  const [formData, setFormData] = useState<DestinationFormData>({
    country: '',
    city: '',
    destinationName: '',
    description: '',
    address: '',
    latitude: '',
    longitude: '',
    estimatedDistanceKm: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [refreshKey]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load countries
      const countriesResponse = await yachtDestinationsService.getCountries();
      if (countriesResponse.success && countriesResponse.data) {
        setCountries(countriesResponse.data.countries);
      }

      // Load all destinations
      const destinationsResponse = await yachtDestinationsService.getDestinations();
      if (destinationsResponse.success && destinationsResponse.data) {
        setDestinations(destinationsResponse.data.destinations);
      }
    } catch (error) {
      console.error('Error loading destinations:', error);
      toast.error('Failed to load destinations');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
    toast.success('Refreshing destinations...');
  };

  const handleOpenCreateDialog = () => {
    setEditingDestination(null);
    setFormData({
      country: '',
      city: '',
      destinationName: '',
      description: '',
      address: '',
      latitude: '',
      longitude: '',
      estimatedDistanceKm: '',
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (destination: YachtDestination) => {
    setEditingDestination(destination);
    setFormData({
      country: destination.country,
      city: destination.city,
      destinationName: destination.destinationName,
      description: destination.description || '',
      address: destination.address || '',
      latitude: destination.location.latitude?.toString() || '',
      longitude: destination.location.longitude?.toString() || '',
      estimatedDistanceKm: destination.location.estimatedDistanceKm?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDestination(null);
    setSaving(false);
  };

  const handleSaveDestination = async () => {
    // Validation
    if (!formData.country || !formData.city || !formData.destinationName) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      if (editingDestination) {
        // Update existing destination
        const result = await yachtDestinationsService.updateDestination(editingDestination.id, {
          destinationName: formData.destinationName,
          description: formData.description || undefined,
          address: formData.address || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          estimatedDistanceKm: formData.estimatedDistanceKm
            ? parseFloat(formData.estimatedDistanceKm)
            : undefined,
        });

        if (result.success) {
          toast.success('Destination updated successfully');
          handleCloseDialog();
          handleRefresh();
        } else {
          toast.error(result.error || 'Failed to update destination');
        }
      } else {
        // Create new destination
        const result = await yachtDestinationsService.createDestination({
          country: formData.country,
          city: formData.city,
          destinationName: formData.destinationName,
          description: formData.description || undefined,
          address: formData.address || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          estimatedDistanceKm: formData.estimatedDistanceKm
            ? parseFloat(formData.estimatedDistanceKm)
            : undefined,
        });

        if (result.success) {
          toast.success('Destination created successfully');
          handleCloseDialog();
          handleRefresh();
        } else {
          toast.error(result.error || 'Failed to create destination');
        }
      }
    } catch (error) {
      console.error('Error saving destination:', error);
      toast.error('An error occurred while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (destination: YachtDestination) => {
    const action = destination.isActive ? 'deactivate' : 'activate';
    if (
      !window.confirm(
        `Are you sure you want to ${action} "${destination.destinationName}"?`
      )
    ) {
      return;
    }

    // Optimistic update
    const newActiveStatus = !destination.isActive;
    setDestinations((prev) =>
      prev.map((d) =>
        d.id === destination.id ? { ...d, isActive: newActiveStatus } : d
      )
    );

    const result = await yachtDestinationsService.toggleDestinationStatus(destination.id);

    if (result.success) {
      toast.success(result.message || `Destination ${action}d successfully`);
      // Update countries stats
      const countriesResponse = await yachtDestinationsService.getCountries();
      if (countriesResponse.success && countriesResponse.data) {
        setCountries(countriesResponse.data.countries);
      }
    } else {
      // Revert optimistic update on error
      setDestinations((prev) =>
        prev.map((d) =>
          d.id === destination.id ? { ...d, isActive: !newActiveStatus } : d
        )
      );
      toast.error(result.error || `Failed to ${action} destination`);
    }
  };

  const handleDeleteDestination = async (destinationId: string, destinationName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${destinationName}"?`)) {
      return;
    }

    // Store the destination for potential rollback
    const deletedDestination = destinations.find((d) => d.id === destinationId);

    // Optimistic delete
    setDestinations((prev) => prev.filter((d) => d.id !== destinationId));

    const result = await yachtDestinationsService.deleteDestination(destinationId);

    if (result.success) {
      toast.success('Destination deleted successfully');
      // Update countries stats
      const countriesResponse = await yachtDestinationsService.getCountries();
      if (countriesResponse.success && countriesResponse.data) {
        setCountries(countriesResponse.data.countries);
      }
    } else {
      // Revert optimistic delete on error
      if (deletedDestination) {
        setDestinations((prev) => [...prev, deletedDestination]);
      }

      if (result.activeYachtCount && result.activeYachtCount > 0) {
        toast.error(
          `Cannot delete destination. ${result.activeYachtCount} yacht(s) are using this destination.`
        );
      } else {
        toast.error(result.error || 'Failed to delete destination');
      }
    }
  };

  const getDestinationsForCity = (country: string, city: string) => {
    return destinations.filter(
      (d) =>
        d.country === country &&
        d.city === city &&
        (showInactiveOnly ? !d.isActive : true) &&
        (searchTerm
          ? d.destinationName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            d.description?.toLowerCase().includes(searchTerm.toLowerCase())
          : true)
    );
  };

  const getCitiesForCountry = (countryName: string): string[] => {
    const citiesSet = new Set<string>();
    destinations
      .filter((d) => d.country === countryName)
      .forEach((d) => citiesSet.add(d.city));
    return Array.from(citiesSet).sort();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredCountries = countries.filter((country) => {
    if (!searchTerm) return true;
    return country.country.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              Yacht Destinations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage destination catalog for yacht destination-based pricing
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}>
              Add Destination
            </Button>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <PublicIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {countries.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Countries
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <LocationCityIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {countries.reduce((sum, c) => sum + c.cityCount, 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cities
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <PlaceIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {destinations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Destinations
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {destinations.filter((d) => d.isActive).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Search and Filters */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Box display="flex" gap={2} alignItems="center">
              <TextField
                fullWidth
                size="small"
                placeholder="Search destinations, cities, or countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showInactiveOnly}
                    onChange={(e) => setShowInactiveOnly(e.target.checked)}
                  />
                }
                label="Show Inactive Only"
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Hierarchical View */}
      {filteredCountries.length === 0 ? (
        <Alert severity="info">No destinations found. Create your first destination to get started.</Alert>
      ) : (
        filteredCountries.map((country) => {
          const cities = getCitiesForCountry(country.country);

          return (
            <Accordion
              key={country.country}
              expanded={expandedCountry === country.country}
              onChange={() =>
                setExpandedCountry(expandedCountry === country.country ? null : country.country)
              }
              sx={{ mb: 2 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <PublicIcon color="primary" />
                  <Typography variant="h6" sx={{ flex: 1 }}>
                    {country.country}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      label={`${country.cityCount} cities`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                    <Chip
                      label={`${country.destinationCount} destinations`}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`${country.activeDestinationCount} active`}
                      size="small"
                      color="success"
                    />
                  </Stack>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {cities.map((city) => {
                  const cityDestinations = getDestinationsForCity(country.country, city);

                  if (cityDestinations.length === 0) return null;

                  return (
                    <Card key={`${country.country}-${city}`} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <LocationCityIcon color="secondary" />
                          <Typography variant="h6">{city}</Typography>
                          <Chip
                            label={`${cityDestinations.length} destinations`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>

                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Destination</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Distance</TableCell>
                                <TableCell>Yachts</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {cityDestinations.map((destination) => (
                                <TableRow key={destination.id} hover>
                                  <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <PlaceIcon fontSize="small" color="action" />
                                      <Typography variant="body2" fontWeight={600}>
                                        {destination.destinationName}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {destination.description || '—'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {destination.location.estimatedDistanceKm ? (
                                      <Chip
                                        label={`${destination.location.estimatedDistanceKm} km`}
                                        size="small"
                                        variant="outlined"
                                        icon={<MapIcon />}
                                      />
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={destination.yachtCount || 0}
                                      size="small"
                                      color={destination.yachtCount ? 'primary' : 'default'}
                                      icon={<BoatIcon />}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={destination.isActive ? 'Active' : 'Inactive'}
                                      size="small"
                                      color={destination.isActive ? 'success' : 'default'}
                                      icon={
                                        destination.isActive ? <CheckCircleIcon /> : <CancelIcon />
                                      }
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Edit">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenEditDialog(destination)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={
                                        destination.isActive ? 'Deactivate' : 'Activate'
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        color={destination.isActive ? 'warning' : 'success'}
                                        onClick={() => handleToggleStatus(destination)}
                                      >
                                        {destination.isActive ? (
                                          <ToggleOffIcon fontSize="small" />
                                        ) : (
                                          <ToggleOnIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          handleDeleteDestination(
                                            destination.id,
                                            destination.destinationName
                                          )
                                        }
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      {/* Create/Edit Destination Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingDestination ? 'Edit Destination' : 'Create New Destination'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country *"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  disabled={!!editingDestination}
                  helperText={editingDestination ? 'Cannot change country' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City *"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={!!editingDestination}
                  helperText={editingDestination ? 'Cannot change city' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Destination Name *"
                  value={formData.destinationName}
                  onChange={(e) => setFormData({ ...formData, destinationName: e.target.value })}
                  placeholder="e.g., Tarkwa Bay, Ilashe Beach"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="Brief description of the destination..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address or location details"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  placeholder="6.4541"
                  inputProps={{ step: '0.0001' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  placeholder="3.4316"
                  inputProps={{ step: '0.0001' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Est. Distance (km)"
                  type="number"
                  value={formData.estimatedDistanceKm}
                  onChange={(e) =>
                    setFormData({ ...formData, estimatedDistanceKm: e.target.value })
                  }
                  placeholder="12.5"
                  inputProps={{ step: '0.1', min: '0' }}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                * Required fields. Destination will be available for yachts in {formData.country || 'the country'} → {formData.city || 'the city'}.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDestination}
            disabled={saving || !formData.country || !formData.city || !formData.destinationName}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : editingDestination ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default YachtDestinationsPage;
